import { cn } from "@/lib/utils";
import { type RefObject, useEffect, useState } from "react";
import { cellSelector } from "./cell-selector";
import { DSD_REVIEW_COPY } from "./copy";

/** 선택 셀이 문서 영역 위로 지나갔는가 (아래로 스크롤해 화면에서 사라진 경우). 아래에 있는 경우는 해당 없음 — 아이콘이 "위로" 라서 */
export const isCellScrolledAbove = (
  container: { top: number },
  cell: { bottom: number },
) => cell.bottom < container.top;

const useSelectedCellScrolledAbove = (
  scrollContainerRef: RefObject<HTMLDivElement>,
  cellId: string | null,
) => {
  const [scrolledAbove, setScrolledAbove] = useState(false);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !cellId) {
      setScrolledAbove(false);
      return;
    }
    const update = () => {
      const cell = container.querySelector(cellSelector(cellId));
      setScrolledAbove(
        cell instanceof HTMLElement &&
          isCellScrolledAbove(
            container.getBoundingClientRect(),
            cell.getBoundingClientRect(),
          ),
      );
    };
    update();
    container.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // 스크롤 없이 셀 위치만 바뀌는 경우 — 패널·목차를 여닫아 문서 폭이 바뀌거나 저장 뒤 다시 렌더될 때
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(container);
    if (container.firstElementChild)
      observer?.observe(container.firstElementChild);
    return () => {
      container.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [scrollContainerRef, cellId]);
  return scrolledAbove;
};

/**
 * 문서 영역 오른쪽 아래의 "선택 셀로" 버튼 (Figma: 44px, 오른쪽 40 · 아래 32). 선택 셀을 지나 아래로 스크롤했을 때만 보이고,
 * 누르면 셀을 클릭했을 때와 같은 자리(30% + 근거 창 자리)로 돌아간다
 */
export const DsdReviewScrollToCellButton = ({
  scrollContainerRef,
  cellId,
  onClick,
  className,
}: {
  scrollContainerRef: RefObject<HTMLDivElement>;
  cellId: string | null;
  onClick: () => void;
  className?: string;
}) => {
  const show = useSelectedCellScrolledAbove(scrollContainerRef, cellId);
  if (!show) return null;
  return (
    <button
      type="button"
      aria-label={DSD_REVIEW_COPY.panel.scrollToCell}
      onClick={onClick}
      className={cn(
        "flex size-11 items-center justify-center rounded-[12px] border border-line-normal bg-background-base text-blue-500 shadow-medium transition-colors hover:bg-background-neutral-alternative",
        className,
      )}
    >
      <svg
        width="19"
        height="24"
        viewBox="0 0 19 24"
        fill="none"
        aria-hidden
        className="size-6"
      >
        <path
          d="M8.16667 22.0505V8.25326L2.95996 13.46C2.54991 13.87 1.88433 13.87 1.47428 13.46C1.06423 13.0499 1.06423 12.3843 1.47428 11.9743L8.47428 4.97428C8.88433 4.56423 9.54991 4.56423 9.95996 4.97428L16.96 11.9743C17.37 12.3843 17.37 13.0499 16.96 13.46C16.5499 13.87 15.8843 13.87 15.4743 13.46L10.2676 8.25326V22.0505C10.2676 22.6304 9.79702 23.1009 9.21712 23.1009C8.63722 23.1009 8.16667 22.6304 8.16667 22.0505ZM17.3838 0C17.9637 0 18.4342 0.470557 18.4342 1.05046C18.4342 1.63035 17.9637 2.10091 17.3838 2.10091H1.05046C0.470557 2.10091 0 1.63035 0 1.05046C0 0.470557 0.470557 0 1.05046 0H17.3838Z"
          fill="currentColor"
        />
      </svg>
    </button>
  );
};
